import type { Layout, Layouts } from 'react-grid-layout';
import {
  DEFAULT_GRAPH_SYMBOL,
  DEFAULT_SYMBOLS,
  DEFAULT_TIMEFRAME,
  GRID_COLUMNS,
  GRID_CONTAINER_PADDING,
  GRID_MARGIN,
  GRID_ROW_HEIGHT
} from '../config/appConfig';
import type {
  LaunchableWindowKind,
  QuoteField,
  WindowKind
} from '../data/protocol/types';
import { uid } from '../shared/format';
import type {
  GraphWindowConfig,
  HeatMapWindowConfig,
  NewsDetailWindowConfig,
  NewsWindowConfig,
  QuoteWindowConfig,
  QuotesWindowConfig,
  WindowConfig,
  WindowInstance
} from './types';

interface DashboardSlots {
  quoteMonitorId?: string;
  graphId?: string;
  heatMapId?: string;
  newsId?: string;
  leftQuoteId?: string;
  rightQuoteId?: string;
}

export const QUOTE_FIELDS: QuoteField[] = [
  'symbol',
  'last',
  'bid',
  'ask',
  'change',
  'changePct',
  'volume',
  'high',
  'low',
  'exchange',
  'time'
];

export const LAUNCHABLE_WINDOW_KINDS: LaunchableWindowKind[] = [
  'quotes',
  'quote',
  'graph',
  'heatmap',
  'news'
];

export const WINDOW_DEFINITIONS: Record<
  WindowKind,
  {
    title: string;
    description: string;
    minW: number;
    minH: number;
    defaultW: number;
    defaultH: number;
    icon: string;
  }
> = {
  quotes: {
    title: 'Quote Monitor',
    description: 'Streaming quote table',
    minW: 6,
    minH: 8,
    defaultW: 14,
    defaultH: 16,
    icon: 'QM'
  },
  quote: {
    title: 'Quote',
    description: 'Single-symbol quote card',
    minW: 3,
    minH: 3,
    defaultW: 4,
    defaultH: 4,
    icon: 'Q'
  },
  graph: {
    title: 'Graph',
    description: 'Price graph with live bars',
    minW: 6,
    minH: 8,
    defaultW: 10,
    defaultH: 16,
    icon: 'G'
  },
  heatmap: {
    title: 'Heat Map',
    description: 'Sector breakdown from live quotes',
    minW: 6,
    minH: 8,
    defaultW: 10,
    defaultH: 16,
    icon: 'HM'
  },
  news: {
    title: 'News',
    description: 'Market news feed',
    minW: 6,
    minH: 6,
    defaultW: 12,
    defaultH: 10,
    icon: 'N'
  },
  newsDetail: {
    title: 'News Detail',
    description: 'Full story view',
    minW: 7,
    minH: 7,
    defaultW: 11,
    defaultH: 12,
    icon: 'D'
  }
};

export function createWindow(
  kind: WindowKind,
  zIndex: number,
  configOverride?: Partial<WindowConfig>,
  titleOverride?: string
): WindowInstance {
  const id = uid(kind);

  const config = {
    ...defaultConfig(kind),
    ...configOverride
  } as WindowConfig;

  return {
    id,
    kind,
    title: titleOverride ?? titleFor(kind, config),
    config,
    zIndex
  };
}

export function titleFor(
  kind: WindowKind,
  _config?: WindowConfig
): string {
  if (kind === 'graph') return 'Graph';
  if (kind === 'heatmap') return 'Heat Map';
  if (kind === 'newsDetail') return 'News Detail';

  return WINDOW_DEFINITIONS[kind].title;
}

export function defaultConfig(
  kind: WindowKind
): WindowConfig {
  if (kind === 'quotes') {
    const config: QuotesWindowConfig = {
      symbols: DEFAULT_SYMBOLS.slice(0, 80),
      fields: QUOTE_FIELDS,
      rowHeight: 30,
      maxRows: 400,
      groupId: 'tech'
    };

    return config;
  }

  if (kind === 'quote') {
    const config: QuoteWindowConfig = {
      symbol: DEFAULT_GRAPH_SYMBOL
    };

    return config;
  }

  if (kind === 'graph') {
    const config: GraphWindowConfig = {
      symbol: DEFAULT_GRAPH_SYMBOL,
      timeframe: DEFAULT_TIMEFRAME,
      limit: 260
    };

    return config;
  }

  if (kind === 'heatmap') {
    const config: HeatMapWindowConfig = {
      maxSymbols: 72
    };

    return config;
  }

  if (kind === 'newsDetail') {
    const config: NewsDetailWindowConfig = {
      newsId: ''
    };

    return config;
  }

  const config: NewsWindowConfig = {
    symbols: [],
    showUnreadOnly: false,
    limit: 100
  };

  return config;
}

export function floatingLayoutItem(
  window: WindowInstance,
  breakpoint: keyof typeof GRID_COLUMNS
): Layout {
  const definition = WINDOW_DEFINITIONS[window.kind];
  const cols = GRID_COLUMNS[breakpoint];
  const width = Math.min(definition.defaultW, cols);

  return {
    i: window.id,
    x: 0,
    y: 0,
    w: width,
    h: definition.defaultH,
    minW: Math.min(definition.minW, cols),
    minH: definition.minH
  };
}

export function buildInitialLayouts(
  windows: WindowInstance[]
): Layouts {
  return buildTiledLayouts(windows);
}

export function buildDashboardLayouts(
  windows: WindowInstance[],
  viewportHeight = getViewportHeight()
): Layouts {
  return Object.fromEntries(
    Object.keys(GRID_COLUMNS).map((breakpoint) => [
      breakpoint,
      dashboardLayoutForBreakpoint(
        windows,
        breakpoint as keyof typeof GRID_COLUMNS,
        viewportHeight
      )
    ])
  ) as Layouts;
}

function dashboardLayoutForBreakpoint(
  windows: WindowInstance[],
  breakpoint: keyof typeof GRID_COLUMNS,
  viewportHeight: number
): Layout[] {
  const cols = GRID_COLUMNS[breakpoint];

  const usableRows = Math.max(
    18,
    Math.floor(
      (
        viewportHeight -
        GRID_CONTAINER_PADDING[1] * 2
      ) /
      (
        GRID_ROW_HEIGHT +
        GRID_MARGIN[1]
      )
    )
  );

  if (
    breakpoint === 'xxs' ||
    breakpoint === 'xs' ||
    breakpoint === 'sm'
  ) {
    return buildMobileDashboardLayout(
      windows,
      cols
    );
  }

  const slots = resolveDashboardSlots(windows);

  const leftWidth = cols >= 24 ? 7 : 6;
  const rightWidth = cols >= 24 ? 7 : 6;
  const centerX = leftWidth;
  const centerWidth = Math.max(
    6,
    cols - leftWidth - rightWidth
  );
  const rightX = leftWidth + centerWidth;

  const compactQuoteHeight = Math.max(
    4,
    Math.min(
      6,
      Math.floor(usableRows * 0.17)
    )
  );

  const leftPrimaryHeight = slots.leftQuoteId
    ? usableRows - compactQuoteHeight
    : usableRows;

  const rightPrimaryHeight = slots.rightQuoteId
    ? usableRows - compactQuoteHeight
    : usableRows;

  const centerTopHeight = slots.heatMapId
    ? Math.max(
        8,
        Math.floor(usableRows * 0.54)
      )
    : usableRows;

  const centerBottomY = slots.graphId
    ? centerTopHeight
    : 0;

  const centerBottomHeight = slots.graphId
    ? Math.max(
        8,
        usableRows - centerTopHeight
      )
    : usableRows;

  let fallbackIndex = 0;

  return windows.map((window) => {
    if (window.id === slots.quoteMonitorId) {
      return withBounds(
        window,
        {
          i: window.id,
          x: 0,
          y: 0,
          w: leftWidth,
          h: leftPrimaryHeight
        },
        cols,
        true
      );
    }

    if (window.id === slots.leftQuoteId) {
      return withBounds(
        window,
        {
          i: window.id,
          x: 0,
          y: slots.quoteMonitorId
            ? leftPrimaryHeight
            : 0,
          w: leftWidth,
          h: slots.quoteMonitorId
            ? compactQuoteHeight
            : usableRows
        },
        cols,
        true
      );
    }

    if (window.id === slots.graphId) {
      return withBounds(
        window,
        {
          i: window.id,
          x: centerX,
          y: 0,
          w: centerWidth,
          h: centerTopHeight
        },
        cols,
        true
      );
    }

    if (window.id === slots.heatMapId) {
      return withBounds(
        window,
        {
          i: window.id,
          x: centerX,
          y: centerBottomY,
          w: centerWidth,
          h: centerBottomHeight
        },
        cols,
        true
      );
    }

    if (window.id === slots.newsId) {
      return withBounds(
        window,
        {
          i: window.id,
          x: rightX,
          y: 0,
          w: rightWidth,
          h: rightPrimaryHeight
        },
        cols,
        true
      );
    }

    if (window.id === slots.rightQuoteId) {
      return withBounds(
        window,
        {
          i: window.id,
          x: rightX,
          y: slots.newsId
            ? rightPrimaryHeight
            : 0,
          w: rightWidth,
          h: slots.newsId
            ? compactQuoteHeight
            : usableRows
        },
        cols,
        true
      );
    }

    const layout = createFallbackDashboardLayout(
      window,
      fallbackIndex,
      cols,
      centerX,
      usableRows
    );

    fallbackIndex += 1;

    return layout;
  });
}

function buildMobileDashboardLayout(
  windows: WindowInstance[],
  cols: number
): Layout[] {
  let y = 0;

  return windows.map((window) => {
    const definition = WINDOW_DEFINITIONS[window.kind];

    const height =
      window.kind === 'quote'
        ? 4
        : Math.max(definition.minH, 10);

    const layout = withBounds(
      window,
      {
        i: window.id,
        x: 0,
        y,
        w: cols,
        h: height
      },
      cols
    );

    y += layout.h;

    return layout;
  });
}

function resolveDashboardSlots(
  windows: WindowInstance[]
): DashboardSlots {
  const quoteWindows = windows.filter(
    (window) => window.kind === 'quote'
  );

  return {
    quoteMonitorId: windows.find(
      (window) => window.kind === 'quotes'
    )?.id,
    graphId: windows.find(
      (window) => window.kind === 'graph'
    )?.id,
    heatMapId: windows.find(
      (window) => window.kind === 'heatmap'
    )?.id,
    newsId: windows.find(
      (window) => window.kind === 'news'
    )?.id,
    leftQuoteId: quoteWindows[0]?.id,
    rightQuoteId: quoteWindows[1]?.id
  };
}

function createFallbackDashboardLayout(
  window: WindowInstance,
  index: number,
  cols: number,
  preferredX: number,
  usableRows: number
): Layout {
  const definition = WINDOW_DEFINITIONS[window.kind];
  const width = Math.min(
    definition.defaultW,
    cols
  );

  const maxX = Math.max(
    cols - width,
    0
  );

  const maxY = Math.max(
    usableRows - definition.defaultH,
    0
  );

  return withBounds(
    window,
    {
      i: window.id,
      x: Math.min(
        preferredX + (index % 2) * 2,
        maxX
      ),
      y: Math.min(
        index * 2,
        maxY
      ),
      w: width,
      h: definition.defaultH
    },
    cols,
    true
  );
}

export function buildTiledLayouts(
  windows: WindowInstance[],
  viewportHeight = getViewportHeight()
): Layouts {
  return Object.fromEntries(
    Object.keys(GRID_COLUMNS).map((breakpoint) => [
      breakpoint,
      tiledLayoutForBreakpoint(
        windows,
        breakpoint as keyof typeof GRID_COLUMNS,
        viewportHeight
      )
    ])
  ) as Layouts;
}

function tiledLayoutForBreakpoint(
  windows: WindowInstance[],
  breakpoint: keyof typeof GRID_COLUMNS,
  viewportHeight: number
): Layout[] {
  const cols = GRID_COLUMNS[breakpoint];

  if (windows.length === 0) {
    return [];
  }

  const usableRows = Math.max(
    10,
    Math.floor(
      (
        viewportHeight -
        GRID_CONTAINER_PADDING[1] * 2
      ) /
      (
        GRID_ROW_HEIGHT +
        GRID_MARGIN[1]
      )
    )
  );

  const preferredColumns =
    windows.length <= 1
      ? 1
      : windows.length <= 2
        ? 2
        : windows.length <= 4
          ? 2
          : windows.length <= 6
            ? 3
            : Math.min(
                4,
                Math.ceil(Math.sqrt(windows.length))
              );

  const widestMinimum = Math.max(
    1,
    ...windows.map((window) =>
      Math.min(
        WINDOW_DEFINITIONS[window.kind].minW,
        cols
      )
    )
  );

  const maxColumnsThatFit = Math.max(
    1,
    Math.floor(cols / widestMinimum)
  );

  const columnCount = Math.min(
    preferredColumns,
    maxColumnsThatFit
  );

  const rowCount = Math.ceil(
    windows.length / columnCount
  );

  const cellWidth = Math.max(
    widestMinimum,
    Math.floor(cols / columnCount)
  );

  const cellHeight = Math.max(
    4,
    Math.floor(usableRows / rowCount)
  );

  return windows.map((window, index) => {
    const definition = WINDOW_DEFINITIONS[window.kind];
    const column = index % columnCount;
    const row = Math.floor(index / columnCount);
    const isLastColumn = column === columnCount - 1;
    const x = column * cellWidth;

    const width = isLastColumn
      ? cols - x
      : cellWidth;

    return withBounds(
      window,
      {
        i: window.id,
        x,
        y: row * cellHeight,
        w: Math.max(1, width),
        h: Math.max(
          Math.min(definition.minH, cellHeight),
          cellHeight
        )
      },
      cols,
      true
    );
  });
}

function withBounds(
  window: WindowInstance,
  layout: Omit<Layout, 'minW' | 'minH'>,
  cols: number,
  allowFitBelowMin = false
): Layout {
  const definition = WINDOW_DEFINITIONS[window.kind];

  const minWidth = Math.min(
    definition.minW,
    cols
  );

  const minHeight = allowFitBelowMin
    ? Math.min(
        definition.minH,
        layout.h
      )
    : definition.minH;

  const width = Math.max(
    minWidth,
    Math.min(layout.w, cols)
  );

  return {
    ...layout,
    x: Math.max(
      0,
      Math.min(
        layout.x,
        Math.max(cols - width, 0)
      )
    ),
    w: width,
    h: Math.max(
      minHeight,
      layout.h
    ),
    minW: minWidth,
    minH: minHeight
  };
}

function getViewportHeight(): number {
  if (typeof window === 'undefined') {
    return 900;
  }

  return window.innerHeight;
}